/** Current privacy policy version — aligned with published policy_documents.version_code. */
export const PRIVACY_POLICY_VERSION = "2026.07.01";

/** Pre-2026 localStorage key — migrated to anonymous ack on read; not referenced in production paths. */
const LEGACY_PRIVACY_DISMISSED_STORAGE_KEY = "privacy_dismissed";

/** @deprecated Legacy key treated consent dismiss the same as accept; cleared on read. */
export const LEGACY_PRIVACY_DISMISSED_KEY = LEGACY_PRIVACY_DISMISSED_STORAGE_KEY;

export const PRIVACY_BANNER_SNOOZE_UNTIL_KEY = "privacy_consent_banner_snooze_until";

/** Anonymous visitors acknowledge the informational banner (per policy version). */
export const PRIVACY_ANON_ACK_VERSION_KEY = "privacy_anon_acknowledged_version";

/** How long "Not now" hides the banner (milliseconds). */
export const PRIVACY_BANNER_SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;

export interface PrivacyConsentRecord {
  consent_date?: string;
  consent_type?: string;
  ip_address?: string;
  location?: {
    city?: string;
    country?: string;
    full?: string;
    [key: string]: unknown;
  } | null;
  metadata?: {
    policy_version?: string;
    [key: string]: unknown;
  } | null;
}

export interface MappedConsentHistoryItem {
  timestamp: string;
  policyVersion: string;
  location: string;
  ipAddress: string;
}

export function clearLegacyPrivacyDismissedKey(): void {
  try {
    localStorage.removeItem(LEGACY_PRIVACY_DISMISSED_STORAGE_KEY);
  } catch {
    // localStorage may be unavailable in private mode / SSR
  }
}

/** Honor legacy dismiss flag by treating it as anonymous acknowledgment. */
export function migrateLegacyPrivacyDismissedToAck(): void {
  try {
    if (localStorage.getItem(LEGACY_PRIVACY_DISMISSED_STORAGE_KEY)) {
      acknowledgeAnonymousPrivacy();
      localStorage.removeItem(LEGACY_PRIVACY_DISMISSED_STORAGE_KEY);
    }
  } catch {
    // localStorage may be unavailable in private mode / SSR
  }
}

export interface PrivacyBannerStorageState {
  isSnoozed: boolean;
  hasAnonymousAcknowledged: boolean;
}

/** Read banner dismissal state synchronously to avoid a first-paint flash. */
export function readPrivacyBannerStorageState(): PrivacyBannerStorageState {
  migrateLegacyPrivacyDismissedToAck();
  return {
    isSnoozed: isPrivacyBannerSnoozed(),
    hasAnonymousAcknowledged: hasAnonymousPrivacyAcknowledged(),
  };
}

export function hasUserAcceptedTerms(
  user: { user_metadata?: Record<string, unknown> } | null | undefined
): boolean {
  const acceptedAt = user?.user_metadata?.terms_accepted_at;
  return typeof acceptedAt === "string" && acceptedAt.length > 0;
}

export function snoozePrivacyBanner(): void {
  try {
    const until = Date.now() + PRIVACY_BANNER_SNOOZE_MS;
    localStorage.setItem(PRIVACY_BANNER_SNOOZE_UNTIL_KEY, String(until));
  } catch {
    // ignore
  }
}

/**
 * Is Privacy Banner Snoozed.
 */
export function isPrivacyBannerSnoozed(): boolean {
  try {
    const raw = localStorage.getItem(PRIVACY_BANNER_SNOOZE_UNTIL_KEY);
    if (!raw) {
      return false;
    }
    const until = Number(raw);
    if (Number.isNaN(until) || Date.now() >= until) {
      localStorage.removeItem(PRIVACY_BANNER_SNOOZE_UNTIL_KEY);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * Map Privacy Consent Records.
 */
export function mapPrivacyConsentRecords(
  records: PrivacyConsentRecord[]
): MappedConsentHistoryItem[] {
  return (records || []).map((consent) => {
    const { location } = consent;
    const locationString = location
      ? location.full || `${location.city || "Unknown"}, ${location.country || "Unknown"}`
      : "Not recorded";

    const policyVersion =
      (consent.metadata &&
        typeof consent.metadata === "object" &&
        consent.metadata.policy_version) ||
      consent.consent_type ||
      "privacy_policy";

    return {
      timestamp: consent.consent_date || "",
      policyVersion: String(policyVersion),
      location: locationString,
      ipAddress: consent.ip_address ? String(consent.ip_address) : "Not recorded",
    };
  });
}

export function hasAnonymousPrivacyAcknowledged(): boolean {
  try {
    return localStorage.getItem(PRIVACY_ANON_ACK_VERSION_KEY) === PRIVACY_POLICY_VERSION;
  } catch {
    return false;
  }
}

export function acknowledgeAnonymousPrivacy(): void {
  try {
    localStorage.setItem(PRIVACY_ANON_ACK_VERSION_KEY, PRIVACY_POLICY_VERSION);
  } catch {
    // localStorage may be unavailable in private mode
  }
}

/**
 * Should Show Privacy Banner.
 */
export function shouldShowPrivacyBanner(options: {
  isAuthenticated: boolean;
  hasConsented: boolean;
  consentLoading: boolean;
  isSnoozed: boolean;
  hasAnonymousAcknowledged?: boolean;
  hasTermsAccepted?: boolean;
}): boolean {
  const {
    isAuthenticated,
    hasConsented,
    consentLoading,
    isSnoozed,
    hasAnonymousAcknowledged,
    hasTermsAccepted,
  } = options;

  if (isSnoozed) {
    return false;
  }

  if (isAuthenticated) {
    if (consentLoading) {
      return false;
    }
    if (hasConsented || hasTermsAccepted) {
      return false;
    }
    return true;
  }

  // Anonymous: informational banner until acknowledged or snoozed
  if (hasAnonymousAcknowledged ?? hasAnonymousPrivacyAcknowledged()) {
    return false;
  }

  return true;
}
